<template>
    <JetDialogModal :show="showModal" @close="closeModal" max-width="3xl" backgroundClass="bg-primaryOrange">
        <template #title>
            <span v-if="currentItem">Modification de l'article: N°{{ currentItem.reference }}</span>
            <span v-else>Ajouter un nouvel article</span>
        </template>

        <template #content>
            <div class="grid grid-cols-3 gap-4 mt-2">
                <div class="col-span-3">
                    <JetLabelRequired for="name" value="Nom de l'article" />
                    <JetInput id="name" type="text" v-model="form.name" autocomplete="off" placeholder="Entrer le nom de l'article" />
                    <JetInputError :message="form.errors.name" />
                </div>

                <div class="col-span-1">
                    <JetLabelRequired for="price" :value="'Prix en ' + currency.symbol" />
                    <JetInput id="price" type="number" min="0" placeholder="0.00"
                        class="text-right" v-model="form.price" autocomplete="off"
                    />
                    <JetInputError :message="form.errors.price" />
                </div>

                <div class="col-span-2">
                    <JetLabel for="category_id" value="Catégorie:" />
                    <Multiselect
                        v-model="form.categories"
                        mode="tags"
                        :close-on-select="false"
                        :searchable="true"
                        :options="categories"
                        placeholder="Sélectionner une ou plusieurs catégories"
                    />
                    <JetInputError :message="form.errors.categories" />
                </div>

                <div class="col-span-3">
                    <JetLabelRequired for="description" value="Description de l'article" />

                    <div class="border rounded-lg overflow-hidden">
                        <div class="flex gap-2 p-2 border-b bg-gray-50">
                            <button
                                type="button"
                                class="px-3 py-1 border rounded text-sm"
                                @click="editor && editor.chain().focus().toggleBold().run()"
                            >
                                Gras
                            </button>


                            <button
                                type="button"
                                class="px-3 py-1 border rounded text-sm"
                                @click="editor && editor.chain().focus().toggleUnderline().run()"
                            >
                                Souligné
                            </button>


                            <button
                                type="button"
                                class="px-3 py-1 border rounded text-sm"
                                @click="editor && editor.chain().focus().toggleBulletList().run()"
                            >
                                Liste
                            </button>


                            <button
                                type="button"
                                class="px-3 py-1 border rounded text-sm"
                                @click="editor && editor.chain().focus().setTextAlign('left').run()"
                            >
                                ⬅
                            </button>


                            <button
                                type="button"
                                class="px-3 py-1 border rounded text-sm"
                                @click="editor && editor.chain().focus().setTextAlign('center').run()"
                            >
                                ⬌
                            </button>


                            <button
                                type="button"
                                class="px-3 py-1 border rounded text-sm"
                                @click="editor && editor.chain().focus().setTextAlign('right').run()"
                            >
                                ➡
                            </button>
                        </div>

                        <EditorContent
                            v-if="editor"
                            :editor="editor"
                            class="text-sm px-4 min-h-[200px] prose max-w-none focus:outline-none"
                        />
                    </div>

                    <JetInputError :message="form.errors.description" />
                </div>
            </div>
        </template>

        <template #footer>
            <BotiButton @click="closeModal" text="Quitter" color="red">
                <template #icon>
                    <i class="fad fa-times-circle text-sm ml-1"></i>
                </template>
            </BotiButton>

            <BotiButton :loader="form.processing" :disabled="form.processing" @click="manage"
                :text="currentItem ? 'Modifier' : 'Enregistrer'" class="ml-2"
                :class="{ 'opacity-25': form.processing }" :color="currentItem ? 'sky' : 'greendack'"
            >
                <svg v-if="form.processing" class="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                    <path
                        d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"
                    />
                </svg>

                <template #icon v-if="currentItem">
                    <i class="fad fa-user-edit text-sm ml-1"></i>
                </template>

                <template #icon v-else>
                    <i class="fad fa-check text-sm ml-1"></i>
                </template>
            </BotiButton>
        </template>
    </JetDialogModal>
</template>

<script>
    import _ from "underscore";
    import { defineComponent } from "vue";
    import JetLabel from "@/Jetstream/Label";
    import JetInput from "@/Jetstream/Input";
    import StarterKit from "@tiptap/starter-kit";
    import BotiButton from "@/Components/Button";
    import Multiselect from "@vueform/multiselect";
    import JetInputError from "@/Jetstream/InputError";
    import Underline from "@tiptap/extension-underline";
    import TextAlign from "@tiptap/extension-text-align";
    import JetDialogModal from "@/Jetstream/DialogModal";
    import { Editor, EditorContent } from "@tiptap/vue-3";
    import JetLabelRequired from "@/Jetstream/LabelRequired";

    export default defineComponent({
        components: {
            JetLabel,
            JetInput,
            BotiButton,
            Multiselect,
            JetInputError,
            EditorContent,
            JetDialogModal,
            JetLabelRequired
        },

        props: {
            modes: Array,
            travel: Object,
            currency: Object,
            categories: Array
        },

        data() {
            return {
                currentItem: null,
                showModal: false,
                editor: null,

                form: this.$inertia.form({
                    name: null,
                    price: null,
                    categories: [],
                    description: null
                }),
            };
        },

        created() {
            const that = this;

            this.emitter.on("showItemModal", function (evt) {
                that.showItemModal(evt);
            });
        },

        mounted() {
            this.editor = new Editor({
                extensions: [
                    StarterKit,
                    Underline,
                    TextAlign.configure({
                        types: ["heading", "paragraph"],
                    }),
                ],

                content: "",

                onUpdate: ({ editor }) => {
                    this.form.description = editor.getHTML();
                },
            });
        },

        beforeUnmount() {
            if (this.editor) {
                this.editor.destroy();
            }
        },

        methods: {
            clearEditor() {
                if (this.editor) {
                    this.editor.commands.clearContent();
                }
            },


            setEditorContent(content = "") {
                if (!this.editor) {
                    return;
                }

                this.editor.commands.setContent(content);
            },


            closeModal() {
                this.showModal = false;
                this.currentItem = null;

                this.form.clearErrors();
                this.form.reset();

                this.clearEditor();
            },


            showItemModal(currentItem) {

                if (!currentItem) {

                    this.currentItem = null;

                    this.form.reset();

                    this.clearEditor();

                } else {

                    this.currentItem = currentItem;

                    this.form.name = currentItem.name;

                    this.form.categories = _.pluck(
                        currentItem.categories,
                        "id"
                    );

                    this.form.price = currentItem.price;

                    this.form.description = currentItem.description;

                    this.$nextTick(() => {
                        this.setEditorContent(
                            currentItem.description ?? ""
                        );
                    });
                }

                this.showModal = true;
            },

            manage() {
                if (this.currentItem) {
                    this.update();
                } else {
                    this.store();
                }
            },

            store() {
                this.form
                    .transform((data) => ({
                        ...data,
                    }))
                    .post(route("travels.items.store", this.travel), {
                        preserveScroll: true,
                        reserveState: true,

                        onSuccess: () => {
                            this.closeModal();

                            this.$swal.fire(
                                "Article",
                                "Article enregistré avec succès.",
                                "success"
                            );
                        },

                        onError: (error) => {
                            console.log(error);
                        },
                    });
            },

            update() {
                this.form
                    .transform((data) => ({
                        ...data,
                    }))
                    .put(
                        route("travels.items.update", {
                            travel: this.travel,
                            item: this.currentItem,
                        }),
                        {
                            preserveScroll: true,
                            reserveState: true,

                            onSuccess: () => {
                                this.closeModal();

                                this.$swal.fire(
                                    "Article",
                                    "Article mis à jour avec succès.",
                                    "success"
                                );
                            },

                            onError: (error) => {
                                console.log(error);
                            },
                        }
                    );
            },
        },
    });
</script>

<style scoped>
    :deep(.ProseMirror) {
        min-height: 200px;
        outline: none;
    }

    :deep(.ProseMirror p) {
        margin-bottom: 0.75rem;
    }

    :deep(.ProseMirror ul) {
        list-style-type: disc;
        padding-left: 1.5rem;
    }

    :deep(.ProseMirror ol) {
        list-style-type: decimal;
        padding-left: 1.5rem;
    }
</style>
